import React from 'react';
import { useTranslation } from 'react-i18next';
import { getRoomUnitTerminology } from '../../../utils/utils';
import { useGetConfigs } from '../../../hooks/useGetConfigs';
import {
  formatBookingDetailDate,
  formatBookingDetailTime,
  isBookingBlock
} from './bookingDetailDisplayHelpers';
import BookingsStatusBadge from './BookingsStatusBadge';
import BookingPaymentBreakdownV2 from './BookingPaymentBreakdownV2';
import {
  DetailsCard,
  DetailsCardItem,
  DetailsCardLabel,
  DetailsCardRow,
  DetailsCardValue,
  DetailsBodySection,
  DetailsCardWrap,
  DetailsMainContent,
  DetailsSectionHeader,
  DetailsSectionTitle,
  DetailBannerImg,
  SecondaryPillButton,
  StatusBadgeWrap
} from './bookingDetailsV2Styles';

function BookingDetailsV2Content({
  booking,
  bannerUrl,
  onBannerError,
  onViewAmenity,
  viewAmenityDisabled
}) {
  const { t } = useTranslation();
  const { config } = useGetConfigs();

  const isBlock = isBookingBlock(booking);
  const buildingLabel = booking?.building || booking?.tenant?.building || '—';
  const residentLabel = booking?.tenant?.name || '—';
  const unitLabel = booking?.tenant?.unit_num || '—';
  const amenityLabel = booking?.amenity_name || booking?.title || '—';
  const reasonLabel = booking?.title || '—';
  const startDateLabel = formatBookingDetailDate(booking?.start_date);
  const endDateLabel = formatBookingDetailDate(booking?.end_date);
  const startTimeLabel = formatBookingDetailTime(booking?.start_date);
  const endTimeLabel = formatBookingDetailTime(booking?.end_date);
  const unitTerm = getRoomUnitTerminology(config);
  const waiverForm = booking?.waiver_form;
  const reservationFormLabel =
    waiverForm?.name?.trim() || waiverForm?.file_name || '—';
  const showPaymentSection =
    !isBlock && !!booking?.require_payment && booking?.cost_breakdown;

  return (
    <DetailsMainContent>
      {bannerUrl ? (
        <DetailBannerImg src={bannerUrl} alt="" onError={onBannerError} />
      ) : null}

      <DetailsBodySection>
        <DetailsSectionHeader>
          <DetailsSectionTitle>
            {isBlock
              ? t('Block Details', { defaultValue: 'Block Details' })
              : t('Booking Details')}
          </DetailsSectionTitle>
          <SecondaryPillButton
            type="button"
            onClick={onViewAmenity}
            disabled={viewAmenityDisabled || !booking?.amenity_id}>
            {t('View Amenity')}
          </SecondaryPillButton>
        </DetailsSectionHeader>

        <DetailsCardWrap>
          <DetailsCard>
          <DetailsCardRow>
            <DetailsCardItem>
              <DetailsCardLabel>{t('Amenity')}</DetailsCardLabel>
              <DetailsCardValue>{amenityLabel}</DetailsCardValue>
            </DetailsCardItem>
            <DetailsCardItem>
              <DetailsCardLabel>{t('Building')}</DetailsCardLabel>
              <DetailsCardValue>{buildingLabel}</DetailsCardValue>
            </DetailsCardItem>
          </DetailsCardRow>
          {!isBlock ? (
            <DetailsCardRow>
              <DetailsCardItem>
                <DetailsCardLabel>{t('Resident')}</DetailsCardLabel>
                <DetailsCardValue>{residentLabel}</DetailsCardValue>
              </DetailsCardItem>
              <DetailsCardItem>
                <DetailsCardLabel>{unitTerm}</DetailsCardLabel>
                <DetailsCardValue>{unitLabel}</DetailsCardValue>
              </DetailsCardItem>
            </DetailsCardRow>
          ) : (
            <DetailsCardRow>
              <DetailsCardItem>
                <DetailsCardLabel>{t('Reason', { defaultValue: 'Reason' })}</DetailsCardLabel>
                <DetailsCardValue>{reasonLabel}</DetailsCardValue>
              </DetailsCardItem>
            </DetailsCardRow>
          )}
          <DetailsCardRow>
            <DetailsCardItem>
              <DetailsCardLabel>{t('Start Date', { defaultValue: 'Start Date' })}</DetailsCardLabel>
              <DetailsCardValue>{startDateLabel}</DetailsCardValue>
            </DetailsCardItem>
            <DetailsCardItem>
              <DetailsCardLabel>{t('End Date', { defaultValue: 'End Date' })}</DetailsCardLabel>
              <DetailsCardValue>{endDateLabel}</DetailsCardValue>
            </DetailsCardItem>
          </DetailsCardRow>
          <DetailsCardRow>
            <DetailsCardItem>
              <DetailsCardLabel>{t('Start Time', { defaultValue: 'Start Time' })}</DetailsCardLabel>
              <DetailsCardValue>{startTimeLabel}</DetailsCardValue>
            </DetailsCardItem>
            <DetailsCardItem>
              <DetailsCardLabel>{t('End Time', { defaultValue: 'End Time' })}</DetailsCardLabel>
              <DetailsCardValue>{endTimeLabel}</DetailsCardValue>
            </DetailsCardItem>
          </DetailsCardRow>
          {!isBlock && waiverForm ? (
            <DetailsCardRow>
              <DetailsCardItem>
                <DetailsCardLabel>
                  {t('add_amenity_review_reservation_form_file', {
                    defaultValue: 'Reservation Form'
                  })}
                </DetailsCardLabel>
                <DetailsCardValue>{reservationFormLabel}</DetailsCardValue>
              </DetailsCardItem>
            </DetailsCardRow>
          ) : null}
          {showPaymentSection ? (
            <BookingPaymentBreakdownV2
              t={t}
              breakdown={booking.cost_breakdown}
              paymentDetails={booking.payment_details}
            />
          ) : null}
          </DetailsCard>
        </DetailsCardWrap>
      </DetailsBodySection>
    </DetailsMainContent>
  );
}

export function BookingDetailStatusBadge({ statusLabel, booking }) {
  return (
    <StatusBadgeWrap>
      <BookingsStatusBadge booking={booking} statusLabel={statusLabel} />
    </StatusBadgeWrap>
  );
}

export default BookingDetailsV2Content;
